import { Box, Link as MuiLink, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { PageState } from '../../shared/data/PageState';
import { DetailHeader } from '../../shared/detail/DetailHeader';
import { MetadataList, type MetadataItem } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { toCommissionLedgerPageError } from './CommissionLedgerPage';
import { commissionLedgerRepositoryToken } from './repository';
import type { CommissionLedgerEntryDetail } from './types';
import { formatExactSignedMoney } from './types';

export function CommissionLedgerDetailPage({ dataSource }: { readonly dataSource: DataSource }) {
  const { entryId = '' } = useParams();
  const repository = useRepository(commissionLedgerRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    (signal) => repository.get(entryId, signal),
    [entryId, refreshKey, repository],
    () => false,
  );
  const permissions =
    auth.status === 'authenticated' ? new Set(auth.principal.permissions) : new Set<string>();

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageState
        error={toCommissionLedgerPageError(resource.error, t('commissionLedger.errors.detail'))}
        onRetry={() => setRefreshKey((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={2}>
            <DetailHeader
              backTo={paths.commissionLedger}
              dataSource={dataSource}
              subtitle={t('commissionLedger.detail.immutable')}
              title={resource.data.id}
            />
            <DetailSection
              items={overviewItems(resource.data, permissions, language, t)}
              title={t('commissionLedger.detail.overview')}
            />
            <DetailSection
              items={sourceItems(resource.data, permissions, t)}
              title={t('commissionLedger.detail.source')}
            />
            <DetailSection
              items={walletItems(resource.data, language, t)}
              title={t('commissionLedger.detail.walletPosting')}
            />
          </Stack>
        )}
      </PageState>
    </Box>
  );
}

function DetailSection({
  items,
  title,
}: {
  readonly items: readonly MetadataItem[];
  readonly title: string;
}) {
  return (
    <Paper component="section" variant="outlined" sx={{ p: 2 }}>
      <Typography component="h2" variant="h5" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <MetadataList items={items} />
    </Paper>
  );
}

function overviewItems(
  entry: CommissionLedgerEntryDetail,
  permissions: ReadonlySet<string>,
  language: TimeLanguage,
  t: ReturnType<typeof useTranslation>['t'],
): readonly MetadataItem[] {
  const effective = formatTimestamp(entry.effectiveAt, 'Asia/Bangkok', language);
  const created = formatTimestamp(entry.createdAt, 'Asia/Bangkok', language);
  const beneficiaryLabel = entry.beneficiaryUsername ?? entry.beneficiaryId;
  const beneficiary =
    entry.beneficiaryType === 'AFFILIATE_MEMBER' && permissions.has('MEMBER_VIEW') ? (
      <MuiLink component={Link} to={`${paths.members}/${encodeURIComponent(entry.beneficiaryId)}`}>
        {beneficiaryLabel}
      </MuiLink>
    ) : entry.beneficiaryType === 'AGENT' && permissions.has('AGENT_VIEW') ? (
      <MuiLink component={Link} to={`${paths.agents}/${encodeURIComponent(entry.beneficiaryId)}`}>
        {beneficiaryLabel}
      </MuiLink>
    ) : (
      beneficiaryLabel
    );
  const agent =
    entry.agentId === null ? (
      t('common.notAvailable')
    ) : permissions.has('AGENT_VIEW') ? (
      <MuiLink component={Link} to={`${paths.agents}/${encodeURIComponent(entry.agentId)}`}>
        {entry.agentCode ?? entry.agentId}
      </MuiLink>
    ) : (
      (entry.agentCode ?? entry.agentId)
    );
  return [
    { label: t('commissionLedger.fields.entryType'), value: entry.entryType },
    {
      label: t('commissionLedger.fields.signedAmount'),
      value: formatExactSignedMoney(entry.signedAmount, entry.currencyCode),
    },
    { label: t('commissionLedger.fields.beneficiaryType'), value: entry.beneficiaryType },
    { label: t('commissionLedger.fields.beneficiary'), value: beneficiary },
    { label: t('commissionLedger.fields.agent'), value: agent },
    {
      label: t('commissionLedger.fields.partner'),
      value: `${entry.partner.code} · ${entry.partner.name}`,
    },
    {
      label: t('commissionLedger.fields.reasonCode'),
      value: entry.reasonCode ?? t('common.notAvailable'),
    },
    { label: t('commissionLedger.fields.reason'), value: entry.reason ?? t('common.notAvailable') },
    {
      label: t('commissionLedger.fields.effectiveAt'),
      value: `${effective.display} (${effective.originalOffset})`,
    },
    {
      label: t('commissionLedger.fields.createdAt'),
      value: `${created.display} (${created.originalOffset})`,
    },
    { label: t('commissionLedger.fields.createdBy'), value: entry.createdBy },
  ];
}

function sourceItems(
  entry: CommissionLedgerEntryDetail,
  permissions: ReadonlySet<string>,
  t: ReturnType<typeof useTranslation>['t'],
): readonly MetadataItem[] {
  const source = entry.source;
  const transaction =
    source.transactionId === null ? (
      t('common.notAvailable')
    ) : permissions.has('TRANSACTION_VIEW') ? (
      <MuiLink
        component={Link}
        to={`${paths.transactions}/${encodeURIComponent(source.transactionId)}`}
      >
        {source.transactionId}
      </MuiLink>
    ) : (
      source.transactionId
    );
  return [
    { label: t('commissionLedger.fields.transactionId'), value: transaction },
    {
      label: t('commissionLedger.fields.partnerTransactionId'),
      value: source.partnerTransactionId ?? t('common.notAvailable'),
    },
    {
      label: t('commissionLedger.fields.calculationId'),
      value: source.calculationId ?? t('common.notAvailable'),
    },
    {
      label: t('commissionLedger.fields.reversalId'),
      value: source.reversalId ?? t('common.notAvailable'),
    },
    {
      label: t('commissionLedger.fields.sourceEntryId'),
      value:
        source.sourceEntryId === null ? (
          t('common.notAvailable')
        ) : (
          <MuiLink
            component={Link}
            to={`${paths.commissionLedger}/${encodeURIComponent(source.sourceEntryId)}`}
          >
            {source.sourceEntryId}
          </MuiLink>
        ),
    },
    {
      label: t('commissionLedger.fields.approvalRequestId'),
      value: source.approvalRequestId ?? t('common.notAvailable'),
    },
    {
      label: t('commissionLedger.fields.adjustmentId'),
      value: source.adjustmentId ?? t('common.notAvailable'),
    },
  ];
}

function walletItems(
  entry: CommissionLedgerEntryDetail,
  language: TimeLanguage,
  t: ReturnType<typeof useTranslation>['t'],
): readonly MetadataItem[] {
  const posting = entry.walletPosting;
  if (posting === null)
    return [{ label: t('commissionLedger.detail.walletPosting'), value: t('common.notAvailable') }];
  const effective = formatTimestamp(posting.effectiveAt, 'Asia/Bangkok', language);
  return [
    { label: t('commissionLedger.fields.walletEntryId'), value: posting.entryId },
    { label: t('commissionLedger.fields.walletId'), value: posting.walletId },
    { label: t('commissionLedger.fields.walletEntryType'), value: posting.entryType },
    {
      label: t('commissionLedger.fields.availableDelta'),
      value: formatExactSignedMoney(posting.availableDelta, entry.currencyCode),
    },
    {
      label: t('commissionLedger.fields.availableAfter'),
      value: formatExactSignedMoney(posting.availableAfter, entry.currencyCode),
    },
    {
      label: t('commissionLedger.fields.effectiveAt'),
      value: `${effective.display} (${effective.originalOffset})`,
    },
  ];
}
